// <house-md-upload> — per-file upload widget. Owns its own XHR, renders
// progress, error, and complete states, then removes itself shortly
// after a successful insert into the editor.
//
// UploadStarter is the internal helper used by ActionHandler and
// DragAndDropHandler to spawn an upload widget without having to know
// the resolved upload URL.

function dispatch(target, name, detail = null, cancelable = false) {
  return target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail, cancelable }))
}

export class UploadStarter {
  constructor(host, file) {
    this.element = host.closest("house-md")
    this.file = file
  }

  upload() {
    const proceed = dispatch(this.element, "house-md:before-upload", { file: this.file }, true)
    if (!proceed) return
    const widget = document.createElement("house-md-upload")
    widget.file = this.file
    widget.uploadsURL = this.uploadsUrl
    this.element.appendChild(widget)
  }

  get uploadsUrl() {
    return (
      this.element.dataset.uploadsUrl ||
      document.head.querySelector("meta[name=house-uploads-url]")?.content ||
      "/uploads"
    )
  }
}

export class Upload extends HTMLElement {
  #file
  #uploadsURL
  #errorMessage

  constructor() {
    super()
    this.percentComplete = 0
    this.status = "pending"
  }

  connectedCallback() {
    this.render()
    this.start()
    this.addEventListener("click", this.onClick)
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick)
  }

  async start() {
    const formData = new FormData()
    formData.append("file", this.file)
    if (this.csrfParamName) formData.append(this.csrfParamName, this.csrfToken)

    this.xhr = new XMLHttpRequest()
    this.xhr.open("POST", this.uploadsURL, true)
    this.xhr.upload.onprogress = this.onProgress
    this.xhr.onload = this.onLoad
    this.xhr.onerror = this.onError
    this.xhr.onabort = this.onAbort
    this.xhr.send(formData)

    this.status = "uploading"
    this.render()
  }

  render() {
    this.setAttribute("status", this.status)
    this.innerHTML = `
      ${this.closeButtonHtml()}
      ${this.fileNameHtml()}
      ${this.progressBarHtml()}
      ${this.errorHtml()}
    `
  }

  closeButtonHtml() {
    return this.status === "failed"
      ? "<button data-md-action='close' class='md-close' aria-label='Close'></button>"
      : ""
  }

  fileNameHtml() {
    return `<div class="md-file">${this.file.name}</div>`
  }

  progressBarHtml() {
    return `<progress aria-label="Uploading file…" class="md-progress-bar" max="100" value="${this.percentComplete}">${this.percentComplete}%</progress>`
  }

  errorHtml() {
    return this.#errorMessage ? `<div class="md-error">${this.#errorMessage}</div>` : ""
  }

  onProgress = (event) => {
    if (event.lengthComputable) this.percentComplete = (event.loaded / event.total) * 100
    this.render()
  }

  onLoad = async () => {
    if (this.xhr.status >= 400) {
      this.status = "failed"
      this.#errorMessage = this.xhr.responseText || "Upload failed"
    } else {
      const payload = JSON.parse(this.xhr.responseText)
      await this.document.insertFile(payload.fileName, payload.fileUrl, payload.mimetype)
      this.status = "complete"
      this.removeAfterDelay()
    }
    this.render()
  }

  onError = () => {
    this.status = "failed"
    this.render()
  }

  onAbort = () => {
    this.status = "aborted"
    this.render()
  }

  onClick = (event) => {
    if (event.target.matches("[data-md-action=close]")) this.remove()
  }

  async removeAfterDelay(ms = 500) {
    await new Promise((resolve) => setTimeout(resolve, ms))
    this.remove()
  }

  get document() {
    return this.closest("house-md").document
  }

  get file() {
    return this.#file
  }

  set file(value) {
    this.#file = value
  }

  get uploadsURL() {
    return this.#uploadsURL
  }

  set uploadsURL(value) {
    this.#uploadsURL = value
  }

  get csrfParamName() {
    return document.head.querySelector("meta[name=csrf-param]")?.content
  }

  get csrfToken() {
    return document.head.querySelector("meta[name=csrf-token]")?.content
  }
}

customElements.define("house-md-upload", Upload)
